<template>
  <div class="parent-profile">
    <validation-observer v-if="!readonly" ref="studentSearch" disabled>
      <div class="student-selection row mx-0 mb-2 align-items-center">
        <div
          class="
            col-12 col-md-4
            px-md-4
            d-flex
            flex-column flex-md-row
            align-items-md-center
            justify-content-md-between
            align-self-stretch
            border-right
            mb-3 mb-md-0
          "
        >
          <validation-provider
            class="search-input"
            rules="required"
            v-slot="{ errors }"
          >
            <custom-input
              v-model="model.studentSie"
              placeholder="Escriba número de SIE"
              :error="errors[0]"
              custom-error-msg="Favor escriba el número de SIE."
            />
          </validation-provider>
          <action-button
            variant="success mt-2 mt-md-0"
            text="Buscar"
            i="fas fa-search"
            @click="searchStudent"
          />
        </div>
        <div class="col-12 col-md-5 d-flex flex-column align-items-center">
          <div class="result-pill">Resultados</div>
          <div v-if="student" class="mb-3 mb-md-0">
            <div class="result-row">
              <span class="label">Nombre:</span>
              <span class="content">
                {{ student.fullName }}
              </span>
            </div>
            <div class="result-row">
              <span class="label">Edad:</span>
              <span class="content">
                {{ student.age }}
              </span>
            </div>
            <div class="result-row">
              <span class="label">SIE:</span>
              <span class="content">
                {{ student.studentSieId }}
              </span>
            </div>
            <div class="result-row">
              <span class="label">Registro:</span>
              <span class="content">
                {{ student.regNumber }}
              </span>
            </div>
            <div class="result-row">
              <span class="label">Estatus:</span>
              <span class="content">
                {{ student.status || '--' }}
              </span>
            </div>
          </div>
          <div v-else class="not-found-result">
            No se encontr&oacute; n&uacute;mero de SIE en sistema
          </div>
        </div>
        <div
          v-if="student"
          class="
            col-12 col-md-3
            d-flex
            flex-column flex-md-row
            justify-content-md-end
          "
        >
          <action-button
            variant="outline-danger mb-2 mb-md-0 mr-md-2"
            text="Cancelar"
            i="fas fa-times-circle"
            @click="cancelSelection"
          />
          <action-button
            variant="success"
            text="Añadir"
            i="fas fa-file-plus"
            @click="addStudent"
          />
        </div>
      </div>
    </validation-observer>

    <template v-if="model.selectedStudents.length">
      <span class="label ml-3">Estudiante(s) Seleccionado(s)</span>
      <parent-student-row
        v-for="(student, idx) of model.selectedStudents"
        :key="`student-row${idx}`"
        class="mt-3"
        :student="student"
        :readonly="readonly"
        @remove="removeStudentHandler(idx)"
      />
    </template>
    <validation-provider
      ref="studentValidator"
      v-slot="{ errors }"
      :rules="{ required: !model.selectedStudents.length }"
    >
      <error
        v-if="errors[0]"
        message="Favor agregar al menos un estudiante."
        variant="no-border ml-3"
      />
    </validation-provider>
  </div>
</template>

<script>
import CustomInput from 'CustomInput';
import ActionButton from 'ActionButton';
import { Error as error } from '@/components/common';
import ParentStudentRow from '@/components/common/user/ParentStudentRow.vue';
import eduespService from '@/services/eduesp';

export default {
  name: 'ParentProfile',
  components: { CustomInput, ActionButton, error, ParentStudentRow },
  model: {
    prop: 'user',
    event: 'user-changed',
  },
  props: {
    user: {
      type: Object,
      default: () => {},
    },
    readonly: {
      type: Boolean,
      default: () => false,
    },
  },
  data: () => ({
    model: {
      studentSie: null,
      selectedStudents: [],
    },
    student: null,
  }),
  async mounted() {
    await this.getStudents();
  },
  methods: {
    async searchStudent() {
      const valid = await this.$refs.studentSearch.validate();
      if (!valid) return;

      this.student = await eduespService.getBasicStudentInformation(
        this.model.studentSie
      );
    },
    async getStudents() {
      if (this.user.userId) {
        this.model.selectedStudents = await eduespService.getParentProfileInformation(
          this.user.userId
        );
      }
    },
    removeStudentHandler(index) {
      this.model.selectedStudents.splice(index, 1);
    },
    async addStudent() {
      if (
        !this.model.selectedStudents.some(
          (x) => x.studentSieId === this.student?.studentSieId
        )
      ) {
        this.model.selectedStudents.push(this.student);
        this.$emit('student-selected', this.student?.studentSieId);
        await this.cancelSelection();
      } else {
        this.Toast(
          'Error',
          'Ya existe un estudiante agregado con este número de SIE',
          'error'
        );
      }
    },
    async cancelSelection() {
      this.student = null;
      this.model.studentSie = null;
      await this.$refs.studentSearch.reset();
    },
    async validate() {
      return (await this.$refs.studentValidator.validate()).valid;
    },
    save() {
      const studentSieIds = this.model.selectedStudents.map((s) =>
        Number(s.studentSieId)
      );
      eduespService.saveParentProfileInformation(
        this.user.userId,
        studentSieIds
      );
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@/sass/config/_colors.scss';

.parent-profile ::v-deep {
  .label {
    font-size: 14px;
    font-weight: normal;
    color: map-get($colors, 'gray-6');
    margin-bottom: 0.25rem;
  }
  .content {
    font-weight: normal;
    font-size: 1rem;
    color: map-get($colors, 'black');
  }
  .result-pill {
    background-color: map-get($colors, 'gray-2');
    padding: 0.25rem 1.5rem;
    border-radius: 50rem;
    color: map-get($colors, 'white');
    margin: 0 auto 1.5rem;
    font-size: 0.9375rem;
    width: fit-content;
  }
  .not-found-result {
    font-weight: normal;
    color: map-get($colors, 'gray-3');
    text-align: center;
  }
  .result-row {
    display: block;
    margin: 0.25rem;
  }

  .search-input {
    width: 70%;
  }

  @media only screen and (max-width: 768px) {
    .search-input {
      width: 100%;
    }
    .border-right {
      border-right: unset !important;
    }
    .result-pill {
      margin-bottom: 1rem;
    }
  }
}
</style>
